import React, { useEffect, useState } from 'react';

import { usePathname } from 'next/navigation';

import Collapse from '@mui/material/Collapse';

import styled from '@emotion/styled';

import { useTranslations } from 'next-intl';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { titleHyphenation } from '@common/themes/styles/hyphenation';

import { isServer } from '@/common/environment';
import Icon from '@/components/common/Icon';
import Tooltip from '@/components/common/Tooltip';

import { replaceHashWithoutScrolling } from '../../common/links';

const Header = styled.h3<{ $small?: boolean }>`
  position: relative;
  font-size: ${({ theme, $small }) => ($small ? theme.fontSizeSm : theme.fontSizeMd)};

  /* Room for the copy link, which can't stick out past a narrow screen */
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-right: 2rem;
  }

  &:hover {
    .copy-link {
      visibility: visible;
    }

    .question-title {
      text-decoration: underline;
    }
  }
`;

const CopyLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  visibility: hidden;

  @media print {
    display: none;
  }

  width: 2rem;
  /* At least the 24px target size */
  height: 1.5rem;
  right: -1rem;
  top: -0.5rem;

  ${({ theme }) => theme.breakpoints.down('md')} {
    right: 0;
  }

  border: none;
  border-radius: 0;
  margin: 0;
  padding: 0;
  overflow: visible;
  background: transparent;
  line-height: normal;
  -webkit-appearance: none;

  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }

  svg {
    fill: ${(props) => props.theme.themeColors.dark} !important;
  }
`;

const TriggerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-basis: ${(props) => props.theme.spaces.s200};
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: ${(props) => props.theme.spaces.s050};
  color: ${(props) => props.theme.linkColor};
  font-size: 1.15em;
  line-height: 1;
  font-weight: ${(props) => props.theme.fontWeightNormal};
  text-align: center;

  @media print {
    display: none;
  }
`;

const QuestionTrigger = styled.button`
  display: flex;
  align-items: flex-start;
  flex-grow: 1;
  width: 100%;
  padding: 0;
  margin: 0 0 1em;
  text-align: left;
  font-size: inherit;
  color: ${(props) => props.theme.themeColors.black};
  font-weight: ${(props) => props.theme.fontWeightBold};
  line-height: ${(props) => props.theme.lineHeightMd};
  ${titleHyphenation}
  border: none;
  overflow: visible;
  background: transparent;
  border-radius: 0;
  -webkit-appearance: none;
`;

const AccordionContent = styled(Collapse)`
  margin-bottom: ${(props) => props.theme.spaces.s300};
  margin-left: ${(props) => props.theme.spaces.s200};

  @media print {
    display: block !important;
    height: auto !important;
    overflow: visible !important;
    visibility: visible !important;
  }
`;

const LinkCopyButton = ({ identifier }: { identifier?: string }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [copyText, setCopyText] = useState(() => t('copy-to-clipboard'));

  const origin = !isServer && window.location.origin ? window.location.origin : '';

  const openTooltip = () => {
    setTooltipOpen(true);
    setCopyText(t('copy-to-clipboard'));
  };

  const onCopy = () => {
    setCopyText(t('copied-to-clipboard'));
  };

  return (
    <CopyToClipboard text={`${origin}${pathname}#q${identifier}`} onCopy={onCopy}>
      <Tooltip
        title={copyText}
        open={tooltipOpen}
        onOpen={openTooltip}
        onClose={() => setTooltipOpen(false)}
        describeChild
      >
        <CopyLink
          as="button"
          id={`tooltip-${identifier}`}
          data-testid="link-copy-btn"
          aria-label={t('copy-to-clipboard')}
          className="copy-link"
        >
          <Icon.Link />
        </CopyLink>
      </Tooltip>
    </CopyToClipboard>
  );
};

interface AccordionHeaderProps {
  children: React.ReactElement<unknown> | string;
  isOpen?: boolean;
  onClick?: () => void;
  identifier?: string;
  small?: boolean;
}

const AccordionHeader = ({
  children,
  onClick,
  isOpen,
  identifier,
  small = false,
}: AccordionHeaderProps) => (
  <Header className={isOpen ? 'is-open' : undefined} $small={small}>
    <QuestionTrigger
      className="question-trigger"
      onClick={onClick}
      aria-expanded={isOpen}
      aria-controls={`#collapse-${identifier}`}
      id={`heading-${identifier}`}
    >
      <TriggerIcon>{isOpen ? '-' : '+'}</TriggerIcon>
      <span className="question-title">{children}</span>
    </QuestionTrigger>
    <LinkCopyButton identifier={identifier} />
  </Header>
);

interface AccordionBodyProps {
  isOpen?: boolean;
  identifier?: string;
  children: React.ReactNode;
}

const AccordionBody = ({ children, isOpen, identifier }: AccordionBodyProps) => (
  <AccordionContent
    in={isOpen}
    role="region"
    id={`#collapse-${identifier}`}
    aria-labelledby={`heading-${identifier}`}
    hidden={!isOpen}
  >
    {children}
  </AccordionContent>
);

interface AccordionItemProps {
  id?: string;
  open?: boolean;
  isOpen?: boolean;
  onClick?: () => void;
  identifier?: string;
  children?: React.ReactNode;
}

const AccordionItem = ({ children, isOpen, onClick, identifier }: AccordionItemProps) => (
  <div id={`q${identifier}`}>
    {React.Children.map(children, (child) => {
      if (React.isValidElement<AccordionHeaderProps>(child) && child.type === AccordionHeader) {
        return React.cloneElement(child, { onClick, isOpen, identifier });
      }

      if (React.isValidElement<AccordionBodyProps>(child) && child.type === AccordionBody) {
        return React.cloneElement(child, { isOpen, identifier });
      }

      return null;
    })}
  </div>
);

interface AccordionProps {
  open?: string;
  children: React.ReactNode;
}

function Accordion(props: AccordionProps) {
  const { open, children } = props;
  const [openItem, setOpenItem] = useState(open);

  useEffect(() => {
    // Read open question id from location.hash.
    // Unfortunately this can't be done in server side because
    // hash is not available there.
    const { hash } = window.location;
    const openNow = hash && hash.length > 2 ? hash.slice(2) : undefined;
    // Initialize from the client-only URL fragment after hydration.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (openNow) setOpenItem(openNow);
  }, []);

  const toggleSection = (id: string) => () => {
    const prevOpen = openItem;
    setOpenItem(id === openItem ? undefined : id);

    // put opened question id into URL hash so URL can be shared
    replaceHashWithoutScrolling(id !== prevOpen ? `q${id}` : undefined);
  };

  return (
    <div className="accordion">
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement<AccordionItemProps>(child) || child.type !== AccordionItem) {
          return null;
        }
        const id = child.props.id ?? `${index}`;
        return React.cloneElement(child, {
          isOpen: child.props.open ? true : openItem === id,
          onClick: toggleSection(id),
          identifier: id,
        });
      })}
    </div>
  );
}

Accordion.Item = AccordionItem;
Accordion.Header = AccordionHeader;
Accordion.Body = AccordionBody;

export default Accordion;
